import { Injectable } from '@angular/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Platform } from '@angular/cdk/platform';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private notificationSubject = new BehaviorSubject<PushNotificationSchema | null>(null);
  public notification$: Observable<PushNotificationSchema | null> = this.notificationSubject.asObservable();

  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$: Observable<string | null> = this.tokenSubject.asObservable();

  constructor(private platform: Platform) { }

  /**
   * Initialize push notifications
   */
  async initializePushNotifications(): Promise<void> {
    // Only initialize on native platforms (iOS/Android)
    if (!this.platform.IOS && !this.platform.ANDROID) {
      console.log('Push notifications are only available on native platforms');
      return;
    }

    try {
      // Request permission to use push notifications
      const permStatus = await PushNotifications.requestPermissions();

      if (permStatus.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        await PushNotifications.register();
        this.addListeners();
      } else {
        console.error('Push notification permission denied');
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  }

  /**
   * Add listeners for push notification events
   */
  private addListeners(): void {
    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token: Token) => {
      console.log('Push registration success, token: ' + token.value);
      this.tokenSubject.next(token.value);
      // Send this token to your backend server
      this.sendTokenToServer(token.value);
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ' + JSON.stringify(error));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push notification received: ', notification);
        this.notificationSubject.next(notification);
        this.handleNotificationReceived(notification);
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        console.log('Push notification action performed', notification);
        this.handleNotificationAction(notification);
      }
    );
  }

  /**
   * Send device token to your backend server
   */
  private sendTokenToServer(token: string): void {
    // TODO: Implement API call to send token to your backend
    // Example:
    // this.http.post('your-api-endpoint/register-device', { token }).subscribe();
    console.log('Sending token to server:', token);
  }

  /**
   * Handle notification received while app is in foreground
   */
  private handleNotificationReceived(notification: PushNotificationSchema): void {
    // You can show a custom in-app notification here
    // or update the UI based on the notification data
    console.log('Notification data:', notification.data);
  }

  /**
   * Handle notification tap action
   */
  private handleNotificationAction(action: ActionPerformed): void {
    const notification = action.notification;

    // Navigate to specific page based on notification data
    // Example:
    // if (notification.data?.page) {
    //   this.router.navigate([notification.data.page]);
    // }

    console.log('Action ID:', action.actionId);
    console.log('Notification data:', notification.data);
  }

  /**
   * Get list of delivered notifications
   */
  async getDeliveredNotifications(): Promise<any> {
    const notificationList = await PushNotifications.getDeliveredNotifications();
    console.log('Delivered notifications', notificationList);
    return notificationList;
  }

  /**
   * Remove all delivered notifications from notification center
   */
  async removeAllDeliveredNotifications(): Promise<void> {
    await PushNotifications.removeAllDeliveredNotifications();
    console.log('All delivered notifications removed');
  }

  /**
   * Check current permission status
   */
  async checkPermissions(): Promise<any> {
    return await PushNotifications.checkPermissions();
  }

  /**
   * Remove all listeners (call this on component destroy)
   */
  removeAllListeners(): void {
    PushNotifications.removeAllListeners();
  }
}